/**
 * Каталог онбординг-подсказок для эпохи 1-2.
 *
 * Каждая подсказка показывается ОДИН РАЗ при первом срабатывании условия.
 * Триггер определяется в `OnboardingDriver` (на основе game state +
 * artifacts store + покупок). Здесь — только тексты + якоря.
 *
 * Голос Сай:
 *   - lowercase, тёплый
 *   - без «не/нет/никогда» (Принцип #1)
 *   - короткие фразы, без терминов
 *   - объясняет ЧТО ПРОИСХОДИТ и КУДА смотреть, не как кнопки работают
 *
 * JB 2026-05-26 переработка:
 *   - Формулировки в continuous tense («при каждом тапе вылетает…»), а
 *     не past-«вот сейчас» — игрок видит tip когда событие УЖЕ повторяется.
 *   - rhythm_explained якорится в 'streak' (бэйдж), не в 'counter'.
 *   - upgrades_appeared триггерится когда впервые ХВАТАЕТ на апгрейд,
 *     не после первой кристаллизации (см. OnboardingDriver).
 *   - artifacts_explained якорится в 'top-banner', срабатывает ДО reveal
 *     overlay'я первого артефакта.
 */
import type { OnboardingTip } from './types.js';

export const ONBOARDING_TIPS: readonly OnboardingTip[] = [
  // ----- Самый первый момент — игрок видит Искру, ещё не тапнул -----
  {
    id: 'first_sight',
    anchor: 'top-banner',
    arrow: 'down',
    title: 'привет.',
    body: [
      'это Искра. она ждёт твоего касания.',
      'нажми на неё — и почувствуй как просыпается свет внутри.',
    ],
    priority: 10,
  },

  // ----- После 1-го тапа — объясняем поток фраз-осознаний -----
  // JB 2026-05-26: continuous tense + последовательность first_phrase →
  // atlas_revealed. Атлас объясняется отдельно с своим якорем.
  {
    id: 'first_phrase',
    anchor: 'spark',
    arrow: 'up',
    body: [
      'смотри — при каждом тапе от Искры поднимается слово или число.',
      'это твои осознания. каждое складывается в Атлас памяти.',
    ],
    priority: 8,
  },

  // ----- Сразу после first_phrase — подсветить кнопку Атласа справа сверху -----
  {
    id: 'atlas_revealed',
    anchor: 'atlas',
    arrow: 'down',
    title: 'атлас памяти',
    body: [
      'здесь живёт всё что ты открыл: фразы, уроки, артефакты, апгрейды.',
      'за каждое первое открытие память благодарит — осознаниями или духобаллами.',
      'прогресс по каждому разделу показывает сколько впереди и что разблокирует следующее.',
    ],
    priority: 8,
  },

  // ----- После 5-streak — про ритм и стрик -----
  // JB 2026-05-26: anchor=streak (бэйдж рядом со счётчиком), не counter.
  {
    id: 'rhythm_explained',
    anchor: 'streak',
    arrow: 'up',
    title: 'ритм',
    body: [
      'этот бэйдж считает твои тапы подряд в ритм.',
      'после 5 подряд звук становится глубже — это твой ритм с Искрой.',
    ],
    priority: 7,
  },

  // ----- При первом появлении кнопки кристаллизации (≥10 ос.) -----
  // JB 2026-05-26: было «достаточно осознаний», но на 10 ос. ещё нет 1
  // духобалла. Переписал на «копится» — описание процесса, не момента.
  {
    id: 'crystallize_appeared',
    anchor: 'crystallize',
    arrow: 'down',
    title: 'кристаллизация',
    body: [
      'внизу появилась кнопка — у тебя копится первый духобалл.',
      'на 100 осознаниях она оживёт, и каждый тап будет приближать к нему.',
      'духобаллы остаются с тобой навсегда — на них открываются Проводники и большие апгрейды.',
    ],
    priority: 9,
  },

  // ----- После первой кристаллизации -----
  {
    id: 'first_crystallization_done',
    anchor: 'counter',
    arrow: 'up',
    body: [
      'первый духобалл твой.',
      'на нём можно открыть Проводника или большой апгрейд. они меняют мир ощутимее малых.',
    ],
    priority: 7,
  },

  // ----- Когда впервые ХВАТАЕТ на любой апгрейд -----
  // JB 2026-05-26: триггер сменился — было «после первой кристаллизации»,
  // теперь «когда awareness достиг цены самого дешёвого открытого апгрейда».
  {
    id: 'upgrades_appeared',
    anchor: 'upgrades',
    arrow: 'down',
    title: 'память пути',
    body: [
      'теперь хватает на первый апгрейд. кнопка в углу.',
      'каждый апгрейд меняет мир физически: Искра ярче, окно ритма шире, кулдаун короче.',
    ],
    priority: 6,
  },

  // ----- После первой покупки апгрейда -----
  {
    id: 'first_upgrade_done',
    anchor: 'spark',
    arrow: 'up',
    body: [
      'видишь? Искра откликнулась. это не просто число — это настоящая память на её теле.',
      'каждый уровень добавляет немного. длинная инвестиция.',
    ],
    priority: 7,
  },

  // ----- Перед первым milestone-артефактом — банер сверху ДО reveal -----
  // JB 2026-05-26: tip должен появляться ДО reveal'а, чтобы игрок понимал
  // что сейчас увидит. Driver задерживает добавление в revealQueue пока
  // tip активен.
  {
    id: 'artifacts_explained',
    anchor: 'top-banner',
    arrow: 'down',
    title: 'память пути',
    body: [
      'сейчас на Искре проявится первый знак.',
      'это артефакты — они отмечают пороги твоего пути и остаются навсегда.',
      'через месяц игры твоя Искра будет нести только твою историю.',
    ],
    priority: 8,
  },
];

/** Quick lookup. */
export const TIPS_BY_ID: ReadonlyMap<string, OnboardingTip> = new Map(
  ONBOARDING_TIPS.map((t) => [t.id, t]),
);
